import React from 'react';
import { Monitor, Link } from 'lucide-react';

import { Button } from './Button/Button';
import { Icon } from './Icon/Icon';
import { IconName } from './Icon/Icon.type';
import { Text } from './Text/Text';
import { Container } from './Container/Container';

interface WebviewToolbarProps {
  currentUrl: string;
  initialUrl?: string;
  onLogsClick?: () => void;
  onScreenSizeClick?: () => void;
  onClose?: () => void;
  className?: string;
  onHomeClick?: () => void;
}

// Utility function to parse URL and extract protocol, domain, and path
const parseUrl = (url: string): { protocol: string; domain: string; port: string; path: string; full: string } => {
  try {
    const urlObj = new URL(url);
    return {
      protocol: urlObj.protocol + '//',
      domain: urlObj.hostname,
      port: urlObj.port ? `:${urlObj.port}` : '',
      path: urlObj.pathname + urlObj.search + urlObj.hash,
      full: url,
    };
  } catch {
    return { protocol: 'https://', domain: url, port: '', path: '', full: url };
  }
};

export const WebviewToolbar: React.FC<WebviewToolbarProps> = ({
  currentUrl,
  initialUrl,
  onLogsClick,
  onScreenSizeClick,
  onClose,
  className = '',
  onHomeClick,
}) => {
  const urlParts = parseUrl(currentUrl || '');

  return (
    <Container
      variant="primary"
      padding="none"
      className={`w-full flex items-center gap-bg-v2-xs px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary ${className}`}
    >
      {/* Navigation Controls */}
      <div className="flex items-center gap-bg-v2-xs">
        {initialUrl && onHomeClick && (
          <Button
            label={<Icon name={IconName.Home} size="sm" variant="secondary" />}
            onClick={onHomeClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Home"
            tooltipPosition="bottom"
          />
        )}
      </div>

      {/* URL Display Area - Replit Style: Domain Box (No Path Edit) */}
      <div className="flex-1 flex items-center min-w-0 mx-bg-v2-md">
        <div className="flex-1 flex items-center bg-bg-secondary border border-border-primary rounded-bg-v2-sm overflow-hidden">
          {/* Lock Icon */}
          <div className="px-bg-v2-md flex-shrink-0" title="Secure connection">
            <Icon name={IconName.Lock} size="xs" variant="secondary" />
          </div>

          {/* Domain Display */}
          <div className="flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm">
            <Link size={12} className="text-text-tertiary" />
            <Text variant="text-xs" weight="medium" color="secondary" as="span">
              {urlParts.domain + (urlParts.port ? `${urlParts.port}` : '')}
            </Text>
          </div>
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-bg-v2-xs">
        {onLogsClick && (
          <Button
            label={<Icon name={IconName.Document} size="sm" variant="secondary" />}
            onClick={onLogsClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Webview logs"
            tooltipPosition="bottom"
          />
        )}
        {onScreenSizeClick && (
          <Button
            label={<Monitor size={16} className="text-text-secondary" />}
            onClick={onScreenSizeClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Screen size"
            tooltipPosition="bottom"
          />
        )}
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-6 h-6 flex items-center justify-center rounded-bg-v2-md hover:bg-bg-secondary active:bg-bg-tertiary text-text-secondary transition-colors"
          aria-label="New tab"
          title="New tab"
        >
          <Icon name={IconName.ExternalLink} size="sm" variant="secondary" />
        </a>
        {onClose && (
          <Button
            label={<Icon name={IconName.XClose} size="sm" variant="secondary" />}
            onClick={onClose}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Close Preview"
            tooltipPosition="bottom"
          />
        )}
      </div>
    </Container>
  );
};
